import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Result, Spin } from "antd";
import {
  isChunkLoadError,
  reloadIfStaleChunkError,
} from "../utils/chunkReload";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Stale-chunk recovery: a new build was deployed while this tab was open,
    // so the chunk filenames in our cached index.html no longer exist on the
    // server. A hard reload pulls the fresh index.html with the new hashes.
    if (reloadIfStaleChunkError(error)) return;

    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and navigate to home
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // While the auto-reload from componentDidCatch is in flight, show a
      // neutral "updating" message instead of flashing the scary error UI.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div
            data-testid="chunk-reload-screen"
            style={{ padding: "50px", textAlign: "center" }}
          >
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Updating to the latest version…</p>
          </div>
        );
      }

      return (
        <div style={{ padding: "50px", maxWidth: "800px", margin: "0 auto" }}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle="An unexpected error occurred. Please try reloading the page."
            extra={[
              <Button type="primary" key="reload" onClick={this.handleReload}>
                Reload Page
              </Button>,
              <Button key="home" onClick={this.handleGoHome}>
                Go to Home
              </Button>,
            ]}
          >
            {process.env.NODE_ENV === "development" && this.state.error && (
              <div style={{ textAlign: "left", marginTop: "20px" }}>
                <details style={{ whiteSpace: "pre-wrap" }}>
                  <summary
                    style={{
                      cursor: "pointer",
                      marginBottom: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    Error Details (Development Only)
                  </summary>
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "15px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      overflow: "auto",
                    }}
                  >
                    <strong>Error:</strong> {this.state.error.message}
                    <br />
                    <br />
                    <strong>Stack Trace:</strong>
                    <pre>{this.state.error.stack}</pre>
                    {this.state.errorInfo && (
                      <>
                        <br />
                        <strong>Component Stack:</strong>
                        <pre>{this.state.errorInfo.componentStack}</pre>
                      </>
                    )}
                  </div>
                </details>
              </div>
            )}
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
